import { AssetSymbol } from './api/asset';

// Rendered marker size (PNG). Rectangle: wider than tall.
const SYMBOL_W = 80;
const SYMBOL_H = 44;

export const ASSET_COLORS = [
  '#27AE60',
  '#F59E0B',
  '#EB5757',
  '#9B51E0',
  '#2F80ED',
  '#9CA3AF',
  '#7B5E57',
  '#2D9CDB',
  '#6FCF97',
  '#E2761B',
  '#4F6D7A',
  '#3B5BA5',
  '#0B2239',
];

// Codes are usually <= 5 letters; longer ones are clipped on the symbol.
export const symbolCodeLabel = (code?: string) =>
  (code || '').trim().toUpperCase().slice(0, 5);

/**
 * Builds the asset type's featureable_data independently (no fabric editor):
 * a small config (color + code) plus a rendered PNG data url used when
 * printing and for the map marker. Asset types are shown as a colored
 * rectangle badge — no icon.
 */
export function buildAssetFeatureableData(color: string, code?: string) {
  return {
    type: 'rect',
    config: { color, code } as AssetSymbol,
    dataurl: renderSymbolDataUrl(color, code),
  };
}

/** Fallback when a finding's type has no configured colour. */
const DEFAULT_FINDING_COLOR = '#FF7272';

export type AssetMarker = {
  /** Asset type colour. */
  color: string;
  /** Code + asset ID, e.g. "LTU-1". */
  label: string;
  /** undefined when the asset type has no inspection checks. */
  checksComplete?: boolean;
  /** Findings tied to the asset: type label + its configured colour. */
  findings?: { type: string; color?: string }[];
};

/**
 * Marker geometry in on-screen pixels, mirrored by assetPrintOverlayService on
 * the API: the type badge at its usual size, a check dot on its corner and one
 * small finding chip row underneath.
 */
const LEFT = 2;
const BOX = { w: 52, h: 20, r: 4 };
const BADGE = 6;
const CHIP = { h: 12, gap: 2, top: 2 };

/**
 * The marker is rasterised at device resolution and drawn at {@link
 * ASSET_MARKER_SCALE}, so one source pixel maps to one screen pixel — resizing
 * a bigger bitmap down is what made the label and chips look fuzzy.
 */
const DPR = Math.max(1, Math.round(window.devicePixelRatio || 1));

export const ASSET_MARKER_SCALE = 1 / DPR;

/**
 * Renders the map marker for one asset: the type-coloured label badge, a
 * check-status dot (green tick when complete, amber when pending) and a chip
 * per tied finding type.
 */
export function renderAssetMarkerDataUrl({
  color,
  label,
  checksComplete,
  findings = [],
}: AssetMarker): string {
  const chipsH = findings.length ? CHIP.top + CHIP.h : 0;
  // Equal padding above and below the badge keeps the image centred on the
  // badge, so the marker sits on the asset's point whether or not it has chips.
  const pad = Math.max(BADGE, chipsH);
  const canvas = document.createElement('canvas');

  canvas.width = (LEFT + BOX.w + BADGE + 1) * DPR;
  canvas.height = (pad * 2 + BOX.h) * DPR;

  const ctx = canvas.getContext('2d');

  if (!ctx) return '';

  // Draw in on-screen pixels; the canvas itself is at device resolution.
  ctx.scale(DPR, DPR);

  // Label badge
  ctx.fillStyle = color;
  roundedRect(ctx, LEFT, pad, BOX.w, BOX.h, BOX.r);
  ctx.fill();

  let fontSize = 11;

  ctx.fillStyle = '#fff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `700 ${fontSize}px sans-serif`;
  while (fontSize > 7 && ctx.measureText(label).width > BOX.w * 0.86) {
    fontSize -= 0.5;
    ctx.font = `700 ${fontSize}px sans-serif`;
  }
  ctx.fillText(label, LEFT + BOX.w / 2, pad + BOX.h / 2 + 1);

  // Check-status dot, on the badge's top-right corner
  if (checksComplete !== undefined) {
    ctx.beginPath();
    ctx.arc(LEFT + BOX.w, pad, BADGE, 0, 2 * Math.PI);
    ctx.fillStyle = checksComplete ? '#28C76F' : '#FF9F43';
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.fillStyle = '#fff';
    ctx.font = `700 ${BADGE + 2}px sans-serif`;
    ctx.fillText(checksComplete ? '✓' : '!', LEFT + BOX.w, pad + 1);
  }

  // One small chip per tied finding
  let chipX = LEFT + 1;
  const chipY = pad + BOX.h + CHIP.top;

  ctx.font = `700 ${CHIP.h * 0.7}px sans-serif`;
  findings.forEach((finding) => {
    const text = (finding.type || '').toUpperCase();
    const chipW = ctx.measureText(text).width + 6;

    ctx.fillStyle = finding.color || DEFAULT_FINDING_COLOR;
    roundedRect(ctx, chipX, chipY, chipW, CHIP.h, 2);
    ctx.fill();

    ctx.fillStyle = '#fff';
    ctx.fillText(text, chipX + chipW / 2, chipY + CHIP.h / 2 + 1);
    chipX += chipW + CHIP.gap;
  });

  return canvas.toDataURL('image/png');
}

/** Rounded-rectangle path helper. */
function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/**
 * Renders a colored rounded rectangle with the asset type code (white,
 * centered) to a PNG data url.
 */
function renderSymbolDataUrl(color: string, code?: string): string {
  const canvas = document.createElement('canvas');

  canvas.width = SYMBOL_W;
  canvas.height = SYMBOL_H;

  const ctx = canvas.getContext('2d');

  if (!ctx) return '';

  const label = symbolCodeLabel(code);

  ctx.fillStyle = color;
  roundedRect(ctx, 1, 1, SYMBOL_W - 2, SYMBOL_H - 2, 8);
  ctx.fill();

  if (label) {
    let fontSize = SYMBOL_H * 0.42;
    const maxWidth = SYMBOL_W * 0.82;

    ctx.fillStyle = '#fff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `700 ${fontSize}px sans-serif`;

    // Shrink until the label fits inside the rectangle.
    while (fontSize > 7 && ctx.measureText(label).width > maxWidth) {
      fontSize -= 1;
      ctx.font = `700 ${fontSize}px sans-serif`;
    }

    ctx.fillText(label, SYMBOL_W / 2, SYMBOL_H / 2 + 1);
  }

  return canvas.toDataURL('image/png');
}
